/**
 * MealCategoryCard Component
 * Displays a meal category with its food items and totals.
 * Supports drag-and-drop reordering within the category and moving items
 * to other categories via a dropdown selector.
 */

import { useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, BookOpen, Save, ChefHat } from "lucide-react";
import { IntakeTable } from "./IntakeTable";
import { IntakeForm } from "./IntakeForm";
import { TemplatePickerDialog } from "./TemplatePickerDialog";
import { RecipePickerDialog } from "./RecipePickerDialog";
import { SaveTemplateDialog } from "./SaveTemplateDialog";
import { getMealCategoryDisplayName } from "../utils";
import type {
  Prijem,
  PrijemKategoria,
  Potravina,
  MealCategoryTotals,
} from "../types";

interface MealCategoryCardProps {
  kategoria: PrijemKategoria;
  items: Prijem[];
  potraviny: Potravina[];
  totals: MealCategoryTotals;
  projectedTotals?: MealCategoryTotals | null;
  isEditMode: boolean;
  /** If omitted, the add food action is hidden (read-only mode). */
  onAddFood?: (
    kategoriaId: number,
    potravinaId: number | null,
    receptId: number | null,
    amount: string,
    notes: string
  ) => Promise<void>;
  /** If omitted, the edit food action is hidden (read-only mode). */
  onEditFood?: (item: Prijem) => void;
  /** If omitted, the delete food action is hidden (read-only mode). */
  onDeleteFood?: (id: number) => Promise<void>;
  /** If omitted, the update food action is hidden (read-only mode). */
  onUpdateFood?: (
    id: number,
    hmotnost: string,
    poznamka: string
  ) => Promise<void>;
  editingItem: Prijem | null;
  setEditingItem: (item: Prijem | null) => void;
  onPreviewChange?: (potravinaId: number | null, amount: string) => void;
  /** klientId needed for template picker pantry matching and food search pantry prioritisation */
  klientId?: number | null;
  /** datum needed for template picker */
  datum?: string;
  /** Called when user drags items to a new order within this category */
  onReorder?: (orderedIds: number[]) => void;
  /** Called when user moves an item to a different category */
  onMoveToKategoria?: (prijemId: number, newKategoriaId: number) => void;
  /** All available meal categories (for the move dropdown) */
  allKategorie?: PrijemKategoria[];
}

export function MealCategoryCard({
  kategoria,
  items,
  potraviny,
  totals,
  projectedTotals,
  isEditMode,
  onAddFood,
  onEditFood,
  onDeleteFood,
  onUpdateFood,
  editingItem,
  setEditingItem,
  onPreviewChange,
  klientId,
  datum,
  onReorder,
  onMoveToKategoria,
  allKategorie = [],
}: MealCategoryCardProps) {
  const { t } = useTranslation();
  const [isAddingFood, setIsAddingFood] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [recipePickerOpen, setRecipePickerOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);

  const handleAddFood = async (
    potravinaId: number | null,
    receptId: number | null,
    amount: string,
    notes: string
  ) => {
    if (!onAddFood) return;
    await onAddFood(kategoria.id, potravinaId, receptId, amount, notes);
    setIsAddingFood(false);
  };

  const categoryName = getMealCategoryDisplayName(
    kategoria.nazov || kategoria.name || ""
  );

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-xl">
          <span className="text-primary">{categoryName}</span>
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            ({items.length} {t("table.items", "items")})
          </span>
        </CardTitle>
        <div className="flex items-center gap-2">
          {/* Template picker button (edit mode only) */}
          {isEditMode && klientId && datum && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTemplatePickerOpen(true)}
            >
              <BookOpen className="h-4 w-4 mr-2" />
              {t("mealLibrary:sablona.use")}
            </Button>
          )}
          {/* Recipe picker button (edit mode only) */}
          {isEditMode && klientId && datum && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRecipePickerOpen(true)}
            >
              <ChefHat className="h-4 w-4 mr-2" />
              {t("mealLibrary:recept.use", "Použiť recept")}
            </Button>
          )}
          {/* Save as template button (edit mode, only when there are items) */}
          {isEditMode && items.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSaveTemplateOpen(true)}
            >
              <Save className="h-4 w-4 mr-2" />
              {t("mealLibrary:sablona.saveAsTemplate", "Save Template")}
            </Button>
          )}
          {isEditMode && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAddingFood(!isAddingFood)}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("intake:addFood", "Add Food")}
            </Button>
          )}
        </div>
      </CardHeader>

      {/* Template picker dialog */}
      {klientId && datum && (
        <TemplatePickerDialog
          open={templatePickerOpen}
          onOpenChange={setTemplatePickerOpen}
          klientId={klientId}
          datum={datum}
          prijemKategoriaId={kategoria.id}
          prijemKategoriaNazov={categoryName}
          onApplied={() => {
            setTemplatePickerOpen(false);
          }}
        />
      )}
      {/* Recipe picker dialog */}
      {klientId && datum && (
        <RecipePickerDialog
          open={recipePickerOpen}
          onOpenChange={setRecipePickerOpen}
          klientId={klientId}
          datum={datum}
          prijemKategoriaId={kategoria.id}
          onApplied={() => setRecipePickerOpen(false)}
        />
      )}

      {/* Save as template dialog */}
      <SaveTemplateDialog
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        items={items}
        defaultName={categoryName}
      />

      <CardContent>
        {/* Add Food Form */}
        {isAddingFood && (
          <IntakeForm
            potraviny={potraviny}
            onSubmit={handleAddFood}
            onCancel={() => {
              setIsAddingFood(false);
              onPreviewChange?.(null, "100");
            }}
            onPreviewChange={onPreviewChange}
            klientId={klientId}
          />
        )}

        {/* Food Items Table */}
        {items.length > 0 ? (
          <>
            <IntakeTable
              items={items}
              isEditMode={isEditMode}
              editingItem={editingItem}
              onEdit={onEditFood}
              onDelete={onDeleteFood}
              onUpdate={onUpdateFood}
              onCancelEdit={() => setEditingItem(null)}
              setEditingItem={setEditingItem}
              onReorder={onReorder}
              onMoveToKategoria={onMoveToKategoria}
              allKategorie={allKategorie}
              currentKategoriaId={kategoria.id}
              klientId={klientId}
            />
            {/* Meal Category Summary */}
            <Card className="bg-muted/30 mt-4">
              <CardContent className="pt-4">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-amber-600">
                      {totals.totalEnergiaKcal}
                    </div>
                    {projectedTotals && (
                      <div className="text-xs font-medium text-primary/50 mt-1">
                        → {projectedTotals.totalEnergiaKcal}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {t("summary.energyKcal", "kcal")}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-blue-600">
                      {totals.totalProteinG}g
                    </div>
                    {projectedTotals && (
                      <div className="text-xs font-medium text-blue-600/50 mt-1">
                        → {projectedTotals.totalProteinG}g
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {t("intake:summary.protein", "Protein")}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-orange-600">
                      {totals.totalTukG}g
                    </div>
                    {projectedTotals && (
                      <div className="text-xs font-medium text-orange-600/50 mt-1">
                        → {projectedTotals.totalTukG}g
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {t("intake:summary.fat", "Fat")}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-teal-600">
                      {totals.totalSacharidG}g
                    </div>
                    {projectedTotals && (
                      <div className="text-xs font-medium text-teal-600/50 mt-1">
                        → {projectedTotals.totalSacharidG}g
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {t("intake:summary.carbs", "Carbs")}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          !isAddingFood && (
            <div className="text-center py-8 text-muted-foreground">
              {t("intake:table.noFoodAdded", "No food items added yet")}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}
